/** @jsxImportSource @emotion/react */
import * as Icons from '@zip/icons';
import * as React from 'react';
import { Constants } from 'global';
import { MerchantApplicationStatus } from 'types/responses/activation';
import * as styles from './ActivationDashboard.styles';

type FaqCardProps = {
  applicationStatus?: MerchantApplicationStatus | null;
};

export function FaqCard({
  applicationStatus,
}: FaqCardProps): React.JSX.Element {
  const [expandedIndex, setExpandedIndex] = React.useState<number | null>(null);

  React.useEffect(() => {
    setExpandedIndex(null);
  }, [applicationStatus]);

  const toggle = (i: number): void =>
    setExpandedIndex(expandedIndex === i ? null : i);

  type FaqItem = {
    question: string;
    answer: React.ReactNode;
  };

  const applicationDeclinedReasonContent: FaqItem = {
    question: 'Why was my merchant application declined?',
    answer: (
      <>
        <p>
          When reviewing new partnerships, Zip considers a range of factors,
          including (but not limited to):
        </p>
        <ul>
          <li>Standard business details and model</li>
          <li>Registered ABN</li>
          <li>Length of time trading</li>
          <li>Annual sales</li>
          <li>Overall business risk</li>
        </ul>
        <p>
          Some businesses may not be eligible due to the nature of their
          products or services.
        </p>
        <p>
          This helps us manage risk and maintain responsible partnerships within
          the payments industry.
        </p>
        <p>
          If your business isn&apos;t approved right now, you&apos;re welcome to
          contact us again in 12 months to check if you&apos;re eligible to
          reapply as a Zip merchant.
        </p>
      </>
    ),
  };

  const prohibitedRestrictedBusinessInfoContent: FaqItem = {
    question: 'Prohibited and Restricted Business Information',
    answer: (
      <p>
        Zip has a number of prohibited and restricted businesses. To view our
        full list{' '}
        <a
          href={`${Constants.merchantHelpCenterBaseUrl}/articles/13872051914255-Prohibited-and-Restricted-Business-Information`}
          target="_blank"
          rel="noopener noreferrer"
        >
          click here
        </a>
        .
      </p>
    ),
  };

  const whyOfferZipContent: FaqItem = {
    question: 'Why should I offer Zip?',
    answer: (
      <>
        <p>
          Join over 60,000 businesses across Australia and New Zealand offering
          customers more flexible ways to pay. With more than 2 million ANZ
          customers, Zip helps you grow your reach, boost sales, and get paid
          faster.
        </p>
        <p>
          <strong>Key benefits:</strong>
        </p>
        <ul>
          <li>No setup or ongoing fees — free to join and easy to start.</li>
          <li>Full payment upfront — get paid via EFT daily.</li>
          <li>
            Fast approval — apply online in minutes and start within 48 hours.
          </li>
          <li>
            Seamless integration — connect Zip to your POS or e-commerce
            platform with free integration support.
          </li>
          <li>Trusted by leading brands across multiple industries.</li>
        </ul>
      </>
    ),
  };

  const customerFeesContent: FaqItem = {
    question: 'What are the fees to our customers?',
    answer: (
      <>
        <p>
          The fees for Zip customers can vary depending on the account type and
          the credit limit approved. Below is a simple breakdown of the fees for
          each account type:
        </p>
        <p>
          <strong>Zip Pay:</strong>
        </p>
        <ul>
          <li>No establishment fee.</li>
          <li>
            Monthly Account Fee: $9.95 (waived if you pay your statement closing
            balance in full by the due date).
          </li>
          <li>Repayments start from $10 a week.</li>
          <li>Interest free always.</li>
        </ul>
        <p>
          <strong>Zip Plus:</strong>
        </p>
        <ul>
          <li>No establishment fee.</li>
          <li>
            Monthly Account Fee: $9.95 (waived if you do not have an outstanding
            balance at the end of the month).
          </li>
          <li>
            Interest:
            <ul>
              <li>13.70% p.a. if your balance is over $1,000.</li>
              <li>No interest if your balance is $1,000 or less.</li>
            </ul>
          </li>
          <li>
            Repayments start from just $25 a month and are determined by the
            account&apos;s closing balance.
          </li>
        </ul>
        <p>
          <strong>Zip Money:</strong>
        </p>
        <ul>
          <li>
            $0 - $99 establishment fee, dependent on the approved credit limit
          </li>
          <li>
            Monthly Account Fee: $9.95 (waived if you do not have an outstanding
            balance at the end of the month).
          </li>
          <li>
            Repayments start from just $50 a month and are determined by the
            account&apos;s closing balance.
          </li>
          <li>
            Any balance outstanding at the expiry of the interest free period
            will be charged at the standard variable rate, 25.9% per annum.
          </li>
        </ul>
        <p>
          To learn more about how Zip charges interest, click{' '}
          <a
            href="https://help.zip.co/hc/en-us/articles/360001583816-Is-there-interest-"
            target="_blank"
            rel="noopener noreferrer"
          >
            here
          </a>
          .
        </p>
      </>
    ),
  };

  const costAndFeesContent: FaqItem = {
    question: 'What cost and fees are involved for offering Zip?',
    answer: (
      <>
        <p>
          It&apos;s free to join Zip — there are no setup costs, ongoing fees,
          or lock-in contracts. You can sign up and start offering Zip to your
          customers at no cost.
        </p>
        <p>
          <strong>The following fees may apply:</strong>
        </p>
        <ul>
          <li>
            Merchant Service Fee (MSF): A percentage of each transaction,
            deducted from your daily disbursement. The exact rate is confirmed
            during your application and outlined in your Merchant Services
            Agreement.
          </li>
          <li>
            Transaction Fee: A flat fee per transaction (if applicable), also
            deducted before funds are transferred to your account.
          </li>
        </ul>
        <p>
          Zip also provides free integration support, tailored to your POS or
          e-commerce platform.
        </p>
      </>
    ),
  };

  const applicationRequirementsContent: FaqItem = {
    question: 'What do I need to have ready before I apply?',
    answer: (
      <>
        <p>
          Your details: Personal details, such as name, contact number and email
          address.
        </p>
        <p>
          <strong>Business details:</strong>
        </p>
        <ul>
          <li>
            Registered Australian Business Number (ABN). This doesn&apos;t
            include Australian Company Number (ACN)
          </li>
          <li>Total annual sales.</li>
          <li>Website URL and e-commerce platform (if online).</li>
          <li>Main business address and business contact number.</li>
        </ul>
        <p>
          Director details: Director&apos;s name, address and contact details. A
          form of ID such as a Driver Licence or Passport will be needed to
          verify identity and confirm association with the business.
        </p>
        <p>
          This Zip Partner application form is for Australian businesses only.
          If you&apos;re based in NZ please visit{' '}
          <a
            href="https://zip.co/nz/for-merchants/"
            target="_blank"
            rel="noopener noreferrer"
          >
            zip.co/nz/for-merchants/
          </a>{' '}
          and if you&apos;re based in US please visit{' '}
          <a
            href="https://zip.co/us/for-businesses"
            target="_blank"
            rel="noopener noreferrer"
          >
            zip.co/us/for-businesses
          </a>{' '}
          for more information. The application takes a few minutes, find out if
          your business is eligible once application is submitted.
        </p>
      </>
    ),
  };

  const customerEligibilityContent: FaqItem = {
    question: 'What is the customer eligibility criteria?',
    answer: (
      <>
        <p>
          To apply for a <strong>Zip Pay, Zip Plus</strong> or{' '}
          <strong>Zip Money </strong>account, customers must meet the following
          requirements:
        </p>
        <ul>
          <li>Be 18 years or older</li>
          <li>Be an Australian citizen or permanent resident</li>
          <li>Have a valid and verifiable email address and mobile number</li>
          <li>Be employed and receiving a regular income</li>
          <li>Have a good credit history</li>
        </ul>
        <p>
          <strong>How customers can apply</strong>
        </p>
        <p>
          If your customer meets the eligibility criteria and would like to use
          Zip to pay for their purchase, you can send them a remote order
          directly from your Zip Merchant Dashboard.
        </p>
        <p>
          This will prompt them to create or sign in to their Zip account and
          complete their application seamlessly.
        </p>
        <p>
          <strong>Important:</strong>
        </p>
        <p>
          As Zip is a line of credit, a credit check may be performed as part of
          the application process. Customers must ensure that all personal
          information provided is accurate and belongs to them — joint accounts
          are not available.
        </p>
      </>
    ),
  };

  const declinedFaqs: FaqItem[] = [
    applicationDeclinedReasonContent,
    prohibitedRestrictedBusinessInfoContent,
  ];

  const approvedFaqs: FaqItem[] = [
    costAndFeesContent,
    whyOfferZipContent,
    customerFeesContent,
    customerEligibilityContent,
  ];

  const defaultFaqs: FaqItem[] = [
    costAndFeesContent,
    applicationRequirementsContent,
    whyOfferZipContent,
    customerFeesContent,
    customerEligibilityContent,
  ];

  const getFaqs = (
    applicationStatus: MerchantApplicationStatus | undefined
  ): FaqItem[] => {
    switch (applicationStatus) {
      case MerchantApplicationStatus.Declined:
        return declinedFaqs;

      case MerchantApplicationStatus.Onboarded:
      case MerchantApplicationStatus.Approved:
        return approvedFaqs;

      default:
        return defaultFaqs;
    }
  };

  const faqs = getFaqs(applicationStatus);

  return (
    <section css={styles.faqCard} aria-labelledby="activation-faq-title">
      <div css={styles.faqContent}>
        <div css={styles.faqIntro}>
          <h2
            className="heading2"
            css={styles.faqTitle}
            id="activation-faq-title"
          >
            Frequently Asked Questions
          </h2>
          <p className="body2" css={styles.faqBody}>
            Have more questions? View our new{' '}
            <a
              className="body1"
              css={styles.faqLink}
              href={Constants.merchantHelpCenterBaseUrl}
              rel="noreferrer"
              target="_blank"
            >
              Merchant help centre
            </a>
            .
          </p>
        </div>

        <div css={styles.faqList}>
          {faqs.map((faq, index) => {
            const isExpanded = expandedIndex === index;
            const questionId = `activation-faq-question-${index}`;
            const answerId = `activation-faq-answer-${index}`;

            return (
              <div
                key={faq.question}
                css={styles.faqItem(index !== faqs.length - 1, isExpanded)}
              >
                <button
                  aria-controls={answerId}
                  aria-expanded={isExpanded}
                  css={styles.faqQuestion(isExpanded)}
                  id={questionId}
                  onClick={(): void => toggle(index)}
                  type="button"
                >
                  <span className="heading3" css={styles.faqQuestionText}>
                    {faq.question}
                  </span>
                  {isExpanded ? (
                    <Icons.ChevronUp
                      aria-hidden="true"
                      css={styles.faqChevron}
                      focusable="false"
                    />
                  ) : (
                    <Icons.ChevronDown
                      aria-hidden="true"
                      css={styles.faqChevron}
                      focusable="false"
                    />
                  )}
                </button>

                {isExpanded && (
                  <div
                    aria-labelledby={questionId}
                    className="body2"
                    css={styles.faqAnswer}
                    id={answerId}
                    role="region"
                  >
                    {faq.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
